import React, { useState, useEffect } from 'react';
import { EyeIcon, CheckCircleIcon, ClockIcon } from '@heroicons/react/24/outline';
import { useNavigate } from 'react-router-dom';

const TeamAppraisals = () => {
  const [teamMembers, setTeamMembers] = useState([]);
  const [selectedCycle, setSelectedCycle] = useState(null);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  const managerEmpCode = localStorage.getItem('emp_code');

  useEffect(() => {
    fetchActiveCycle();
  }, []);

  const fetchActiveCycle = async () => {
    try {
      const response = await fetch('http://localhost:8000/api/appraisal/active-cycle');
      const data = await response.json();
      setSelectedCycle(data.cycle_id);
      fetchTeamAppraisals(data.cycle_id);
    } catch (error) {
      console.error('Error fetching active cycle:', error);
      setLoading(false);
    }
  };

  const fetchTeamAppraisals = async (cycleId) => {
    try {
      const response = await fetch(`http://localhost:8000/api/manager/team-appraisals/${managerEmpCode}/${cycleId}`);
      const data = await response.json();
      setTeamMembers(data);
    } catch (error) {
      console.error('Error fetching team appraisals:', error);
    } finally {
      setLoading(false);
    }
  };

  const getStatusBadge = (status) => {
    const badges = {
      'GOALS_ASSIGNED': { bg: 'bg-gray-100', text: 'text-gray-800', label: 'Goals Assigned' },
      'SELF_SUBMITTED': { bg: 'bg-yellow-100', text: 'text-yellow-800', label: 'Pending Review' },
      'MANAGER_REVIEWED': { bg: 'bg-blue-100', text: 'text-blue-800', label: 'Reviewed' },
      'RELEASED': { bg: 'bg-green-100', text: 'text-green-800', label: 'Released' }
    };
    const badge = badges[status] || badges['GOALS_ASSIGNED'];
    return (
      <span className={`px-3 py-1 rounded-full text-xs font-medium ${badge.bg} ${badge.text}`}>
        {badge.label}
      </span>
    );
  };

  const handleReview = (empCode) => {
    navigate(`/manager/appraisal/review/${empCode}/${selectedCycle}`);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-gray-600">Loading team appraisals...</div>
      </div>
    );
  }

  return (
    <div className="p-6 max-w-7xl mx-auto">
      <div className="bg-white rounded-lg shadow-md p-6">
        <h1 className="text-2xl font-bold text-gray-800 mb-6">Team Appraisals</h1>

        {teamMembers.length === 0 ? (
          <div className="text-center py-12">
            <ClockIcon className="h-16 w-16 text-gray-400 mx-auto mb-4" />
            <p className="text-gray-600">No team members found for appraisal</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Employee</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Designation</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Self Rating</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Manager Rating</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {teamMembers.map((member) => (
                  <tr key={member.emp_code} className="hover:bg-gray-50">
                    <td className="px-6 py-4">
                      <div>
                        <div className="font-medium text-gray-900">{member.name}</div>
                        <div className="text-sm text-gray-500">{member.emp_code}</div>
                      </div>
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-600">{member.designation}</td>
                    <td className="px-6 py-4">{getStatusBadge(member.status)}</td>
                    <td className="px-6 py-4 text-sm text-gray-600">
                      {member.self_rating ? `${member.self_rating}/5.0` : '-'}
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-600">
                      {member.manager_rating ? `${member.manager_rating}/5.0` : '-'}
                    </td>
                    <td className="px-6 py-4">
                      <button
                        onClick={() => handleReview(member.emp_code)}
                        className="flex items-center gap-2 text-indigo-600 hover:text-indigo-800"
                      >
                        <EyeIcon className="h-5 w-5" />
                        {member.status === 'SELF_SUBMITTED' ? 'Review' : 'View'}
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

export default TeamAppraisals;
